import { SellingZoneGraphItem } from '@substratahq/selling-zone/typing/base';
import React from 'react';
import { DetailedAnalysisEmail } from '../../../../../store/deals/deals.types';
import { EmailSectionSkeleton } from '../skeletons/EmailSectionSkeleton';
import { Assessment } from './components/assessment/Assessment';
import { EmailMessage } from './components/emailMessage/EmailMessage';
import { EmailFooter } from './components/footer/EmailFooter';
import { EmailHeader } from './components/header/EmailHeader';
import { NextMove } from './components/nextMove/NextMove';
import { ReplyTime } from './components/replyTime/ReplyTime';
import { YourSentiment } from './components/sentiment/YourSentiment';
import { Topics } from './components/topics/Topics';
import { UpperHand } from './components/upperHand/UpperHand';
import { WordCount } from './components/wordCount/WordCount';

type Props = {
  currentEmail: DetailedAnalysisEmail | null;
  emailsInBranch?: number;
  nextMoveText?: string;
  isThreadComplete?: boolean;
  onLeftArrowClick: () => void;
  onRightArrowClick: () => void;
  selectedGraphItem?: SellingZoneGraphItem | null;
  goToLastMessage: () => void;
  isLoading: boolean;
  firstReplyIndex: number;
};

export const EmailSection: React.FC<Props> = ({
  currentEmail,
  emailsInBranch,
  nextMoveText,
  isThreadComplete,
  onLeftArrowClick,
  onRightArrowClick,
  selectedGraphItem,
  goToLastMessage,
  isLoading,
  firstReplyIndex,
}): JSX.Element => {
  const sender = currentEmail?.sender;

  const emailDate = currentEmail?.sentAt;

  const emailMessage = currentEmail?.text;

  const emailTopics = currentEmail?.topics;

  const participants = currentEmail?.participants;

  const dominantParticipant = currentEmail?.dominantParticipant;

  const relativeReplyTime =
    currentEmail?.assessmentInsights?.lastEmailRelativeMetrics
      ?.relativeReplyTime;

  const assesmentInsights =
    currentEmail?.assessmentInsights?.assessmentInsights;

  const sentiment = currentEmail?.sentiment?.mag
    ? currentEmail.sentiment.mag
    : null;

  const relativeWordCount =
    currentEmail?.assessmentInsights?.lastEmailRelativeMetrics
      ?.relativeWordCount;

  const isNextMove =
    typeof nextMoveText === 'string' && nextMoveText?.length > 0;

  const isEmailBeforeReplyCheck =
    typeof currentEmail?.index === 'number'
      ? currentEmail.index < firstReplyIndex || firstReplyIndex === 0
      : false;

  const attrs = {
    wrapper: { className: 'email' },

    animationSection: {
      className: 'email__email-section-for-animation',
    },

    header: {
      wrapper: { className: 'email__header' },

      Header: {
        from: sender,
        sentAt: emailDate,
        recipients: currentEmail?.recipients,
        isDottedEnvelope: isEmailBeforeReplyCheck,
      },
    },

    body: {
      wrapper: { className: 'email__body' },

      firstColumn: {
        wrapper: { className: 'email__body__first-column' },
        EmailMessage: { message: emailMessage },
        Topics: { topics: emailTopics },
      },

      secondColumn: {
        wrapper: { className: 'email__body__second-column' },

        UpperHand: {
          participants,
          dominant: dominantParticipant,
          currentEmail,
        },

        ReplyTime: {
          replyTime: relativeReplyTime,
          participants,
        },

        Assessment: {
          assesments: assesmentInsights,
        },
      },

      divider: {
        className: 'email__body-divider',
      },

      thirdColumn: {
        wrapper: { className: 'email__body__third-column' },

        YourSentiment: {
          sentiment,
          participants,
        },

        WordCount: {
          wordCount: relativeWordCount,
          participants,
        },

        NextMove: {
          currentEmail,
          emailsInBranch,
          nextMoveMessage: nextMoveText,
          isThreadComplete,
          selectedGraphItem,
          goToLastMessage,
        },
      },
    },

    footer: {
      currentEmail,
      isThreadComplete,
      emailsInBranch,
      onLeftArrowClick,
      onRightArrowClick,
      selectedGraphItem,
      isNextMove,
    },
  };

  return (
    <EmailSectionSkeleton isLoading={isLoading}>
      <div {...attrs.wrapper}>
        <div {...attrs.animationSection}>
          <EmailHeader {...attrs.header.Header} />
          <div {...attrs.body.wrapper}>
            <div {...attrs.body.firstColumn.wrapper}>
              <EmailMessage {...attrs.body.firstColumn.EmailMessage} />
              <Topics {...attrs.body.firstColumn.Topics} />
            </div>
            <div {...attrs.body.secondColumn.wrapper}>
              <UpperHand {...attrs.body.secondColumn.UpperHand} />
              <ReplyTime {...attrs.body.secondColumn.ReplyTime} />
              <Assessment {...attrs.body.secondColumn.Assessment} />
            </div>
            <div {...attrs.body.divider} />
            <div {...attrs.body.thirdColumn.wrapper}>
              <YourSentiment {...attrs.body.thirdColumn.YourSentiment} />
              <WordCount {...attrs.body.thirdColumn.WordCount} />
              <NextMove {...attrs.body.thirdColumn.NextMove} />
            </div>
          </div>
        </div>
        <EmailFooter {...attrs.footer} />
      </div>
    </EmailSectionSkeleton>
  );
};
